"use client";

import { useEffect, useState } from "react";

interface PerfBadgeProps {
    /** Server render duration in milliseconds — measured on the server component. */
    serverMs: number;
    /** Byte size of the hardcoded dataset (approximate). */
    payloadKb?: number;
}

interface ClientTiming {
    ttfb: number | null;
    fcp: number | null;
    lcp: number | null;
    domInteractive: number | null;
}

/**
 * Bottom-right floating pill with render/paint timings.
 *
 * Only mounts when `?perf=1` is in the URL — no cost otherwise. Server render
 * time is passed as a prop; client-side paints are read from PerformanceObserver
 * once the browser has them.
 */
export function PerfBadge({ serverMs, payloadKb }: PerfBadgeProps) {
    const [timing, setTiming] = useState<ClientTiming>({
        ttfb: null,
        fcp: null,
        lcp: null,
        domInteractive: null,
    });

    useEffect(() => {
        const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        setTiming((t) => ({
            ...t,
            ttfb: nav ? Math.round(nav.responseStart - nav.requestStart) : null,
            domInteractive: nav ? Math.round(nav.domInteractive - nav.startTime) : null,
        }));

        const paintObs = new PerformanceObserver((entries) => {
            for (const e of entries.getEntries()) {
                if (e.name === "first-contentful-paint") {
                    setTiming((t) => ({ ...t, fcp: Math.round(e.startTime) }));
                }
            }
        });
        paintObs.observe({ type: "paint", buffered: true });

        const lcpObs = new PerformanceObserver((entries) => {
            const last = entries.getEntries().at(-1);
            if (last) setTiming((t) => ({ ...t, lcp: Math.round(last.startTime) }));
        });
        lcpObs.observe({ type: "largest-contentful-paint", buffered: true });

        return () => {
            paintObs.disconnect();
            lcpObs.disconnect();
        };
    }, []);

    return (
        <div
            className="fixed bottom-4 right-4 z-50 rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2 shadow-lg font-mono text-[11px] leading-tight text-foreground min-w-[180px]"
            role="complementary"
            aria-label="Performance metrics"
        >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Perf · hardcoded</div>
            <Metric label="server" value={`${serverMs.toFixed(1)}ms`} />
            <Metric label="TTFB" value={fmt(timing.ttfb)} />
            <Metric label="FCP" value={fmt(timing.fcp)} />
            <Metric label="LCP" value={fmt(timing.lcp)} />
            <Metric label="DOM interactive" value={fmt(timing.domInteractive)} />
            {payloadKb != null && <Metric label="mock payload" value={`${payloadKb.toFixed(1)}KB`} />}
        </div>
    );
}

function Metric({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

function fmt(v: number | null): string {
    return v == null ? "…" : `${v}ms`;
}
